import { Typography } from '@material-ui/core';
import { Link } from 'gatsby';
import React from 'react';
import {
  ArrowRight,
  Check,
  Cpu,
  Eye,
  MessageCircle,
  Terminal,
  Zap
} from 'lucide-react';
import { DemoButton } from '../components/DemoButton';
import { Headline } from '../components/Headline';
import { Kicker } from '../components/Kicker';
import { Reveal } from '../components/motion';
import { EarlyAccessBadge, NoCodeBadge } from '../components/NewBadge';
import { SEO } from '../components/SEO';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';

const PageHero = styled('div')`
  text-align: center;
  max-width: 900px;
  margin: 140px auto 80px;
  padding: 0 24px;

  @media (max-width: 800px) {
    margin: 100px auto 60px;
  }
`;

const HeroBadgeRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const HeroDescription = styled('p')`
  font-size: 19px;
  line-height: 1.6;
  color: #4f4f4f;
  max-width: 740px;
  margin: 24px auto 36px;
`;

const FeatureSection = styled('section')<{ flip?: boolean }>`
  max-width: 1100px;
  margin: 0 auto;
  padding: 60px 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  direction: ${(p) => (p.flip ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
    direction: ltr;
  }
`;

const FeatureContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const FeatureIconWrapper = styled('div')`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background-color: #e6f7ff;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
`;

const CheckList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 15px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const VisualPanel = styled('div')`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 12px 40px rgba(14, 28, 43, 0.08);
`;

const MonoHeader = styled('div')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #1890ff;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
`;

const PromptLine = styled('div')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 13px;
  line-height: 1.7;
  color: #0e1c2b;
  padding: 10px 14px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }

  span.prefix {
    color: #1890ff;
    margin-right: 8px;
  }
`;

const WhiteCard = styled('div')`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 12px;

  .title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
    margin-bottom: 6px;
  }

  .value {
    font-size: 15px;
    font-weight: 700;
    color: #0e1c2b;
  }

  .caption {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
  }
`;

const ClientChips = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ClientChip = styled('span')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 12px;
  font-weight: 600;
  color: #0e1c2b;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 100px;
  padding: 4px 12px;
`;

const InlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 700px;
  margin: 80px auto 120px;
  padding: 0 24px;
`;

export default () => {
  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/ai-platform/"
          title="The Affilimate AI Platform for Commerce Publishers"
          description="Affilimate Agent, MCP, agentic campaign management, agent analytics for commerce content, and Yield Optimizer: one AI platform built on the infrastructure premium publishers already trust."
        />

        <PageHero>
          <Reveal>
            <Kicker>The Affilimate AI Platform</Kicker>
            <HeroBadgeRow>
              <Headline component="h1" style={{ margin: 0 }}>
                Intelligent automation on the data you already trust
              </Headline>
            </HeroBadgeRow>
            <HeroBadgeRow>
              <EarlyAccessBadge />
            </HeroBadgeRow>
            <HeroDescription>
              Affilimate is the analytics and campaign platform premium
              publishers already run on, now with AI built in. Ask questions in
              plain language, connect your AI tools, and review campaign drafts
              that were scoped before your team opened the app.
            </HeroDescription>
            <DemoButton text="Get a demo" variant="contained" />
          </Reveal>
        </PageHero>

        {/* Affilimate Agent */}
        <FeatureSection>
          <Reveal>
            <FeatureContent>
              <FeatureIconWrapper>
                <MessageCircle size={22} />
              </FeatureIconWrapper>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Affilimate Agent
              </Headline>
              <FeatureBody>
                Your AI analyst inside Affilimate. It works on the same live
                data as your reports (transactions, content, merchants, and
                product inventory) and answers with structure, numbers, and
                links to the underlying views.
              </FeatureBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  Ask why revenue moved, which pages to update, or which
                  merchants are trending in plain language
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Context-aware on the surface you're looking at, from a
                  content page to a campaign draft
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Answers link back to real reports, so your team can verify
                  and go deeper
                </CheckItem>
              </CheckList>
            </FeatureContent>
          </Reveal>
          <Reveal delay={150}>
            <VisualPanel>
              <MonoHeader>Affilimate Agent</MonoHeader>
              <PromptLine>
                <span className="prefix">&gt;</span>
                Which advertisers drove our revenue growth this month?
              </PromptLine>
              <WhiteCard>
                <div className="title">Agent answer</div>
                <div className="value">
                  Wayfair +38%, Samsung +21%, Lululemon +12%
                </div>
                <div className="caption">
                  Based on 4,211 transactions · View advertiser report →
                </div>
              </WhiteCard>
            </VisualPanel>
          </Reveal>
        </FeatureSection>

        {/* MCP */}
        <FeatureSection flip>
          <Reveal>
            <FeatureContent>
              <FeatureIconWrapper>
                <Terminal size={22} />
              </FeatureIconWrapper>
              <HeroBadgeRow style={{ justifyContent: 'flex-start', margin: 0 }}>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  Affilimate MCP
                </Headline>
                <NoCodeBadge />
              </HeroBadgeRow>
              <FeatureBody>
                More than chat with a CSV. Affilimate MCP connects Claude,
                Cursor, and ChatGPT to the full stack: commissions from 100+
                networks, page-level performance, catalog-linked inventory, and
                your custom dimensions — in one query, with zero engineering
                work.
              </FeatureBody>
              <ClientChips>
                {['Claude', 'ChatGPT', 'Codex', 'Cursor', 'Gemini'].map(
                  (chip) => (
                    <ClientChip key={chip}>{chip}</ClientChip>
                  )
                )}
              </ClientChips>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  Secure OAuth connection, set up from Settings in minutes
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Ask cross-layer questions: out-of-stock products on top
                  pages, author P&amp;L, clicks vs. commissions
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Combine with your other tools for research and reporting
                  workflows
                </CheckItem>
              </CheckList>
              <InlineLink to="/mcp/">
                Explore Affilimate MCP <ArrowRight size={18} />
              </InlineLink>
            </FeatureContent>
          </Reveal>
          <Reveal delay={150}>
            <VisualPanel>
              <MonoHeader>Example prompts</MonoHeader>
              <PromptLine>
                <span className="prefix">&gt;</span>
                Which top pages link to out-of-stock products?
              </PromptLine>
              <PromptLine>
                <span className="prefix">&gt;</span>
                Break down Q2 earnings by author and flag declining sections
              </PromptLine>
              <PromptLine>
                <span className="prefix">&gt;</span>
                Compare outdoor gear commission rates across our top merchants
              </PromptLine>
            </VisualPanel>
          </Reveal>
        </FeatureSection>

        {/* AI-assisted campaigns */}
        <FeatureSection>
          <Reveal>
            <FeatureContent>
              <FeatureIconWrapper>
                <Cpu size={22} />
              </FeatureIconWrapper>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                AI-assisted campaigns
              </Headline>
              <FeatureBody>
                Describe a sponsorship in chat and get a complete campaign
                draft: the merchant, the relevant pages from your own
                inventory, and the campaign setup, aligned with your editorial
                standards. Nothing launches without your review.
              </FeatureBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  Drafts scoped to pages already earning in the category
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Every draft lands in Campaigns for your team to review,
                  adjust, and launch
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Built for lean commerce teams: the scoping work is done before
                  you open the app
                </CheckItem>
              </CheckList>
            </FeatureContent>
          </Reveal>
          <Reveal delay={150}>
            <VisualPanel>
              <MonoHeader>Campaign draft</MonoHeader>
              <WhiteCard style={{ marginTop: 0 }}>
                <div className="title">Ready for review</div>
                <div className="value">Bose · March sponsored content</div>
                <div className="caption">
                  12 pages scoped · Flat spend + CPA · Auto-reverts Apr 1
                </div>
              </WhiteCard>
              <WhiteCard>
                <div className="title">Why these pages</div>
                <div className="caption">
                  Top organic performers for premium audio with existing
                  purchase intent, matched via your product catalog inventory.
                </div>
              </WhiteCard>
            </VisualPanel>
          </Reveal>
        </FeatureSection>

        {/* Agent analytics for commerce content */}
        <FeatureSection flip>
          <Reveal>
            <FeatureContent>
              <FeatureIconWrapper>
                <Eye size={22} />
              </FeatureIconWrapper>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Agent analytics for commerce content
              </Headline>
              <FeatureBody>
                AI systems are reading, citing, and sending readers to your
                content. See that activity on the same pages where you already
                track clicks and commissions, so you know where your influence
                is building and what it&apos;s worth.
              </FeatureBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  AI activity alongside revenue, on the same page-level view
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Spot which monetized pages AI assistants rely on most
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  AI crawls and citations tracked on the pages you monetize
                </CheckItem>
              </CheckList>
            </FeatureContent>
          </Reveal>
          <Reveal delay={150}>
            <VisualPanel>
              <MonoHeader>AI discovery · /best-espresso-machines</MonoHeader>
              <WhiteCard style={{ marginTop: 0 }}>
                <div className="title">AI crawls</div>
                <div className="value">4,820 this month</div>
                <div className="caption">+62% vs last month</div>
              </WhiteCard>
              <WhiteCard>
                <div className="title">Page revenue</div>
                <div className="value">$8,140 this month</div>
                <div className="caption">
                  Same page, same view. Influence and earnings together.
                </div>
              </WhiteCard>
            </VisualPanel>
          </Reveal>
        </FeatureSection>

        {/* Yield Optimizer */}
        <FeatureSection>
          <Reveal>
            <FeatureContent>
              <FeatureIconWrapper>
                <Zap size={22} />
              </FeatureIconWrapper>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Yield Optimizer
              </Headline>
              <FeatureBody>
                Cross-merchant swap opportunities surfaced from your
                catalog-linked inventory, with AI-powered campaign suggestions
                waiting at the top of the page, already scoped to the relevant
                content. Part of the Amplify campaigns solution.
              </FeatureBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  Opportunity matrix across every merchant pair you could swap
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  AI suggestions arrive as ready-to-review campaign drafts
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  You approve every swap. Optimization you control, not
                  outsource
                </CheckItem>
              </CheckList>
              <InlineLink to="/product/amplify/">
                Explore Amplify campaigns <ArrowRight size={18} />
              </InlineLink>
            </FeatureContent>
          </Reveal>
          <Reveal delay={150}>
            <VisualPanel>
              <MonoHeader>Yield Optimizer</MonoHeader>
              <WhiteCard style={{ marginTop: 0 }}>
                <div className="title">Suggested campaign</div>
                <div className="value">Wayfair swap · +$14.5K est. lift</div>
                <div className="caption">11 pages scoped · Review draft →</div>
              </WhiteCard>
              <WhiteCard>
                <div className="title">Monthly opportunity volume</div>
                <div className="value">+$77.9K</div>
                <div className="caption">Across 23K monetizable clicks</div>
              </WhiteCard>
            </VisualPanel>
          </Reveal>
        </FeatureSection>

        <FinalCta>
          <Reveal>
            <Headline component="h2">
              See the AI platform on your own data
            </Headline>
            <HeroDescription>
              Get a demo with your integrations in mind, and see what the
              Agent finds in the first conversation.
            </HeroDescription>
            <DemoButton text="Get a demo" variant="contained" />
          </Reveal>
        </FinalCta>
      </div>
    </MainLayout>
  );
};
